/**
 * useTusUpload - Resumable file upload hook using TUS protocol
 *
 * Uses Supabase Storage's native TUS endpoint for resumable uploads.
 * Files are uploaded in 6MB chunks and can be paused, resumed, or
 * recovered after network failures or browser crashes.
 *
 * @see https://supabase.com/docs/guides/storage/uploads/resumable-uploads
 */
import { useCallback, useRef, useState } from 'react';
import * as tus from 'tus-js-client';
import { supabase } from '../lib/supabase';

/** Minimum file size to use TUS (6MB). Below this, standard upload is faster. */
const TUS_THRESHOLD_BYTES = 6 * 1024 * 1024;

/** Supabase requires exactly 6MB chunks for TUS uploads. */
const CHUNK_SIZE = 6 * 1024 * 1024;

export type TusUploadStatus =
  | 'idle'
  | 'uploading'
  | 'paused'
  | 'complete'
  | 'error';

export interface TusUploadState {
  /** 0–100 real progress from TUS byte tracking */
  progress: number;
  status: TusUploadStatus;
  /** Upload speed in bytes/sec (rolling average) */
  bytesPerSecond: number;
  /** Bytes transferred so far */
  bytesUploaded: number;
  /** Total file size in bytes */
  bytesTotal: number;
  /** Error message if status === 'error' */
  error?: string;
  /** Storage path once upload completes */
  storagePath?: string;
}

export interface TusUploadActions {
  /** Start or resume a TUS upload */
  startUpload: (
    file: File,
    bucket: string,
    objectPath: string,
    options?: { upsert?: boolean; cacheControl?: string }
  ) => Promise<string>;
  /** Pause the current upload (can be resumed later) */
  pause: () => void;
  /** Resume a paused upload */
  resume: () => void;
  /** Cancel and abort the upload */
  cancel: () => void;
  /** Whether TUS should be used for a given file size */
  shouldUseTus: (fileSize: number) => boolean;
}

const INITIAL_STATE: TusUploadState = {
  progress: 0,
  status: 'idle',
  bytesPerSecond: 0,
  bytesUploaded: 0,
  bytesTotal: 0,
};

/**
 * Derive the TUS endpoint URL from the Supabase project URL.
 * Input:  https://xyz.supabase.co
 * Output: https://xyz.supabase.co/storage/v1/upload/resumable
 */
function getTusEndpoint(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not defined in environment variables.');
  }
  return `${supabaseUrl.replace(/\/$/, '')}/storage/v1/upload/resumable`;
}

export function useTusUpload(): [TusUploadState, TusUploadActions] {
  const [state, setState] = useState<TusUploadState>(INITIAL_STATE);
  const uploadRef = useRef<tus.Upload | null>(null);
  const speedTracker = useRef<{ startTime: number; lastBytes: number }>({
    startTime: 0,
    lastBytes: 0,
  });

  const shouldUseTus = useCallback((fileSize: number) => {
    return fileSize >= TUS_THRESHOLD_BYTES;
  }, []);

  const startUpload = useCallback(
    async (
      file: File,
      bucket: string,
      objectPath: string,
      options?: { upsert?: boolean; cacheControl?: string }
    ): Promise<string> => {
      // Get current auth session for the authorization header
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        const errorMsg = 'Not authenticated — cannot start upload';
        setState((s) => ({ ...s, status: 'error', error: errorMsg }));
        throw new Error(errorMsg);
      }

      const endpoint = getTusEndpoint();

      return new Promise<string>((resolve, reject) => {
        speedTracker.current = { startTime: Date.now(), lastBytes: 0 };

        setState({
          progress: 0,
          status: 'uploading',
          bytesPerSecond: 0,
          bytesUploaded: 0,
          bytesTotal: file.size,
        });

        const upload = new tus.Upload(file, {
          endpoint,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          chunkSize: CHUNK_SIZE,
          removeFingerprintOnSuccess: true,

          headers: {
            authorization: `Bearer ${session.access_token}`,
            ...(options?.upsert ? { 'x-upsert': 'true' } : {}),
          },

          metadata: {
            bucketName: bucket,
            objectName: objectPath,
            contentType: file.type || 'application/octet-stream',
            cacheControl: options?.cacheControl ?? '3600',
          },

          onProgress: (bytesUploaded: number, bytesTotal: number) => {
            const elapsed = (Date.now() - speedTracker.current.startTime) / 1000;
            const bytesSinceStart = bytesUploaded - speedTracker.current.lastBytes;
            const speed = elapsed > 0 ? bytesSinceStart / elapsed : 0;
            const progress = Math.round((bytesUploaded / bytesTotal) * 100);

            setState({
              progress,
              status: 'uploading',
              bytesPerSecond: speed,
              bytesUploaded,
              bytesTotal,
            });
          },

          onSuccess: () => {
            uploadRef.current = null;
            setState((s) => ({
              ...s,
              progress: 100,
              status: 'complete',
              storagePath: objectPath,
            }));
            resolve(objectPath);
          },

          onError: (err: Error | tus.DetailedError) => {
            uploadRef.current = null;
            const message = err.message || 'Upload failed';
            setState((s) => ({
              ...s,
              status: 'error',
              error: message,
            }));
            reject(new Error(message));
          },
        });

        uploadRef.current = upload;

        // Check for previous incomplete uploads (crash recovery)
        upload.findPreviousUploads().then((previousUploads) => {
          if (previousUploads.length > 0) {
            console.log(
              `[TUS] Resuming previous upload for ${file.name} (${previousUploads.length} found)`
            );
            upload.resumeFromPreviousUpload(previousUploads[0]!);
          }
          upload.start();
        });
      });
    },
    []
  );

  const pause = useCallback(() => {
    if (uploadRef.current) {
      uploadRef.current.abort();
      setState((s) => ({ ...s, status: 'paused' }));
    }
  }, []);

  const resume = useCallback(() => {
    if (uploadRef.current) {
      setState((s) => ({
        ...s,
        status: 'uploading',
        error: undefined,
      }));
      speedTracker.current = {
        startTime: Date.now(),
        lastBytes: state.bytesUploaded,
      };
      uploadRef.current.start();
    }
  }, [state.bytesUploaded]);

  const cancel = useCallback(() => {
    if (uploadRef.current) {
      uploadRef.current.abort(true); // true = delete from server
      uploadRef.current = null;
      setState(INITIAL_STATE);
    }
  }, []);

  return [
    state,
    { startUpload, pause, resume, cancel, shouldUseTus },
  ];
}
